/**
 * Box base URLs.
 *
 * Some customer networks block box.com outright. For those OEMs the
 * `boxProxyEnabled` flag routes every Box request through our own domain
 * instead, where the redirection-worker forwards it on (see
 * `redirection-worker/src/box.ts`).
 *
 * Box uses several hosts for what looks like one operation, so a single flag
 * has to produce a whole set of bases — the API, the upload host, and the CDN
 * the Content Preview SDK is served from.
 */

export interface BoxBaseUrls {
  baseUrl: string;
  uploadBaseUrl: string;
  previewCdnBaseUrl: string;
}

/** Path prefix the redirection-worker serves the Box proxy on. */
const BOX_PROXY_PATH = "/box-api";

/**
 * Origin the proxy lives on. Defaults to the origin of our GraphQL endpoint,
 * so dev/prod resolve correctly without extra deploy config.
 */
const resolveProxyOrigin = (): string | null => {
  const explicit = import.meta.env.VITE_APP_BOX_PROXY_ORIGIN;
  if (explicit) return explicit.replace(/\/+$/, "");

  // VITE_APP_GRAPHQL_ENDPOINT is optional and may be a relative path, in which
  // case this resolves to the app's own origin — same-origin is the better
  // outcome anyway, since it needs no CORS. The worker serves /box-api on both
  // the app and api hosts so either resolution works.
  const graphqlEndpoint = import.meta.env.VITE_APP_GRAPHQL_ENDPOINT;
  if (!graphqlEndpoint) return window.location.origin;

  try {
    return new URL(graphqlEndpoint, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

/**
 * Base URLs for Box. Returns `undefined` when the OEM is not proxied, which is
 * what every consumer wants — the library and the direct callers all fall back
 * to Box's own hosts when the value is undefined.
 */
export const getBoxBaseUrls = (
  proxyEnabled: boolean | null | undefined,
): BoxBaseUrls | undefined => {
  if (!proxyEnabled) return undefined;

  const origin = resolveProxyOrigin();
  if (!origin) return undefined;

  const proxyBase = `${origin}${BOX_PROXY_PATH}`;

  return {
    baseUrl: `${proxyBase}/2.0`,
    uploadBaseUrl: `${proxyBase}/upload/2.0`,
    previewCdnBaseUrl: `${proxyBase}/static`,
  };
};

/** Box's own API base, for callers that build URLs directly. */
export const DEFAULT_BOX_API_BASE_URL = "https://api.box.com/2.0";
